import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import { SeoHead } from '@/components/SeoHead';


export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [status, setStatus] = useState<'checking' | 'ready' | 'invalid'>('checking');
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    // Listen for the recovery event (fires when supabase processes the URL)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (!cancelled) setStatus('ready');
      }
    });

    (async () => {
      const url = new URL(window.location.href);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const code = url.searchParams.get('code');
      const errorDescription =
        url.searchParams.get('error_description') || hash.get('error_description');
      const hashType = hash.get('type');
      const queryType = url.searchParams.get('type');

      if (errorDescription) {
        setErrorMsg(decodeURIComponent(errorDescription.replace(/\+/g, ' ')));
        setStatus('invalid');
        return;
      }

      // PKCE flow: ?code=...
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);
        if (cancelled) return;
        if (error) {
          setErrorMsg(error.message);
          setStatus('invalid');
        } else {
          setStatus('ready');
        }
        return;
      }

      // Implicit/hash flow: #access_token=...&type=recovery — supabase auto-detects
      if (hashType === 'recovery' || queryType === 'recovery') {
        // Wait briefly for onAuthStateChange to flip status; fall back to session check
      }

      // If a session already exists (e.g. user clicked from a fresh tab), allow reset
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (session) {
        setStatus('ready');
        return;
      }

      // Give the auth listener a moment to fire after URL parsing
      setTimeout(async () => {
        if (cancelled) return;
        const { data: { session: s2 } } = await supabase.auth.getSession();
        if (s2) setStatus('ready');
        else setStatus('invalid');
      }, 1500);
    })();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setSuccess(true);
      toast.success('Password updated successfully!');
      // Sign out so they re-authenticate with the new password
      setTimeout(async () => {
        await supabase.auth.signOut();
      }, 800);
    } catch (err: any) {
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title="Reset Your Password — Sportara"
        description="Securely set a new password for your Sportara account and get back to your Game IQ training in just a few seconds."
        path="/reset-password"
        noindex
      />

      <div className="bg-secondary text-secondary-foreground py-8 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Logo className="w-14 h-14" alt="Sportara" />
        </div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Sportara — Reset Your Password</h1>
        <p className="text-sm text-secondary-foreground/60 font-body mt-1">
          Reset your password
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm space-y-6"
        >
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-display font-bold text-2xl">Password Updated!</h2>
              <p className="text-sm text-muted-foreground font-body">
                Your password has been reset successfully. Sign in with your new password to continue.
              </p>
              <Button
                onClick={() => (window.location.href = '/')}
                className="w-full h-12 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
              >
                Go to Sign In
              </Button>
            </div>
          ) : status === 'checking' ? (
            <div className="text-center space-y-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 mx-auto animate-pulse" />
              <p className="text-sm text-muted-foreground font-body">Verifying reset link…</p>
            </div>
          ) : status === 'invalid' ? (
            <div className="text-center space-y-4">
              <h2 className="font-display font-bold text-2xl">Invalid or Expired Link</h2>
              <p className="text-sm text-muted-foreground font-body">
                {errorMsg || 'This password reset link is no longer valid. Please request a new one from the sign-in screen.'}
              </p>
              <Button
                onClick={() => (window.location.href = '/')}
                variant="outline"
                className="w-full h-12 text-base font-display font-bold rounded-xl border-2"
              >
                Back to Sign In
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h2 className="font-display font-bold text-2xl">Set New Password</h2>
                <p className="text-sm text-muted-foreground mt-1 font-body">
                  Enter your new password below
                </p>
              </div>

              <form onSubmit={handleReset} className="space-y-3">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pl-10 pr-10 h-12 rounded-xl border-2 font-body"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pl-10 h-12 rounded-xl border-2 font-body"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-base font-display font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
